import React, { useCallback, useMemo, useState } from 'react';
import { Check, ShieldCheck } from 'lucide-react';

import { loadRolePermissions, updateRolePermissions } from './api';
import {
  Empty, Failed, Head, Loading, Pill, SearchField,
} from './parts';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import {
  notificationTopicLabel, permissionDescription, permissionLabel, roleLabel,
} from './labels';

// Who may do what, as one grid.
//
// This lives on its own page rather than under Staff because it is a different
// question with a different blast radius: Staff is about one person at a time,
// this changes every account holding a role at once. ROLE_PERMISSIONS_MANAGE is
// what puts it in the sidebar.

export default function XomicsPermissions() {
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(() => loadRolePermissions(), []);
  const matrix = useLiveResource(['accounts'], load);
  const permissions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = matrix.data?.permissions ?? [];
    if (needle === '') return rows;
    return rows.filter((permission) => (
      `${permission.code} ${permissionLabel(permission.code)} ${permissionDescription(permission.code)}`
        .toLowerCase().includes(needle)
    ));
  }, [matrix.data, query]);

  async function toggle(role, permissionCode) {
    setError(''); setNotice(''); setSaving(role.code);
    const next = role.permissions.includes(permissionCode)
      ? role.permissions.filter((code) => code !== permissionCode)
      : [...role.permissions, permissionCode];
    try {
      const result = await updateRolePermissions(role.code, next);
      setNotice(result.preferenceRowsCreated > 0
        ? tx('ui.permissions.role_permissions_saved_with_notifications', {
          role: roleLabel(role.code),
          added: result.added.length,
          removed: result.removed.length,
          notifications: result.preferenceRowsCreated,
        })
        : tx('ui.permissions.role_permissions_saved', {
          role: roleLabel(role.code),
          added: result.added.length,
          removed: result.removed.length,
        }));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      // Refetched either way: a refusal leaves the server unchanged, and the
      // checkbox has to go back to the truth rather than to what was clicked.
      matrix.refresh();
      setSaving('');
    }
  }

  return (
    <div className="xo-permissions-page">
      <Head
        title={tx("ui.permissions.permissions_d06d5557")}
        subtitle={tx("ui.permissions.what_each_role_may_do_every_account_holding_the_role_i_657b4dc6")}
      />
      <section className="xo-permissions-surface">
        <header className="xo-permissions-toolbar">
          <div className="xo-permissions-title">
            <span aria-hidden="true"><ShieldCheck /></span>
            <div>
              <h2>{tx("ui.permissions.what_each_role_may_do_d3137043")}</h2>
              <p>{tx("ui.permissions.granting_a_permission_opens_the_endpoints_it_guards_an_fff98f27")}</p>
            </div>
          </div>
          <SearchField
            className="xo-permissions-search"
            value={query}
            onChange={setQuery}
            placeholder={tx("ui.permissions.permission_17857134")}
          />
        </header>

        {(notice || error) && (
          <div className={`xo-permissions-notice ${error ? 'is-error' : 'is-success'}`} role="status">
            {error || notice}
          </div>
        )}

        {matrix.loading && matrix.data === null && <Loading />}
        {matrix.error && matrix.data === null && <Failed message={matrix.error} onRetry={matrix.refresh} />}
        {matrix.data && permissions.length === 0 && (
          <Empty
            title={tx('ui.staff.nothing_matches_17e7aebf')}
            detail={tx('ui.staff.try_a_different_search_or_filter_4e653834')}
          />
        )}
        {matrix.data && permissions.length > 0 && (
          <div className="xo-permissions-matrix-scroll">
            <div className="xo-permissions-matrix"
              style={{ '--xo-permission-role-count': matrix.data.roles.length }}>
              <div className="xo-permissions-matrix-head">
                <div className="xo-permission-heading">
                  <strong>{tx("ui.permissions.permission_17857134")}</strong>
                  <span>{permissions.length} / {matrix.data.permissions.length}</span>
                </div>
                {matrix.data.roles.map((role) => (
                  <div key={role.code} className="xo-permission-role-head">
                    <strong>{roleLabel(role.code)}</strong>
                    <span>{role.accounts === 1
                      ? tx('ui.permissions.one_account', { count: role.accounts })
                      : tx('ui.permissions.many_accounts', { count: role.accounts })}</span>
                  </div>
                ))}
              </div>

              <div className="xo-permissions-matrix-body">
                {permissions.map((permission) => (
                  <article className="xo-permission-row" key={permission.code}>
                    <div className="xo-permission-copy">
                      <strong>{permissionLabel(permission.code)}</strong>
                      <span>{permissionDescription(permission.code)}</span>
                      {permission.unlocksTopics.length > 0 && (
                        <div className="xo-permission-topics">
                          {permission.unlocksTopics.map((topic) => (
                            <Pill key={topic.key} tone="info">{notificationTopicLabel(topic.key)}</Pill>
                          ))}
                        </div>
                      )}
                    </div>
                    {matrix.data.roles.map((role) => {
                      const checked = role.permissions.includes(permission.code);
                      return (
                        <label className="xo-permission-toggle" key={role.code}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={saving === role.code}
                            aria-label={tx("ui.permissions.label_for_label2_53e4feb0", { label: permissionLabel(permission.code), label2: roleLabel(role.code) })}
                            onChange={() => toggle(role, permission.code)}
                          />
                          <span className={checked ? 'is-checked' : ''} aria-hidden="true">
                            <Check />
                          </span>
                        </label>
                      );
                    })}
                  </article>
                ))}
              </div>
            </div>
          </div>
        )}

        <footer className="xo-permissions-foot">
          {tx("ui.permissions.changes_apply_to_every_account_with_that_role_immediat_1ed7baa8")}
        </footer>
      </section>
    </div>
  );
}
